import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import "./ScheduleInterviewPage.scss";

// ── APIs ──────────────────────────────────────────────────────────────────────
const EMP_SCHEDULE_API = `${process.env.REACT_APP_API_EMPLOYEE_URL}/interview-schedules`;
const EMP_INTERVIEW_HISTORY_API = `${process.env.REACT_APP_API_EMPLOYEE_URL}/interview-history`;
const ATTENDANCE_MONTHLY_API = `${process.env.REACT_APP_COMMON_API_URL}/attendance/employee-monthly`;

const SLOT_STATUS_BADGE = {
  AVAILABLE: { label: "Available", color: "#16a34a", bg: "#dcfce7" },
  BOOKED: { label: "Booked", color: "#2563eb", bg: "#dbeafe" },
  BLOCKED: { label: "Blocked", color: "#dc2626", bg: "#fee2e2" },
  RESCHEDULED: { label: "Rescheduled", color: "#d97706", bg: "#fef3c7" },
};

const INTERVIEW_STATUS_OPTIONS = [
  { value: "SHORTLISTED", label: "Shortlisted" },
  { value: "REJECTED", label: "Rejected" },
  { value: "HOLD", label: "Hold" },
];

// ── Duration Quick-Select Options (in minutes) ────────────────────────────────
const DURATION_OPTIONS = [
  { label: "15 min", minutes: 15 },
  { label: "30 min", minutes: 30 },
  { label: "45 min", minutes: 45 },
  { label: "1 hr", minutes: 60 },
  { label: "1.5 hr", minutes: 90 },
  { label: "2 hr", minutes: 120 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const fmtForInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

const avatarColor = (name = "") => {
  const c = [
    "#6366f1",
    "#0ea5e9",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
  ];
  return c[(name.charCodeAt(0) || 0) % c.length];
};

/**
 * Calculates duration in minutes between two datetime-local strings.
 * Returns null if invalid.
 */
const calcDurationMinutes = (startStr, endStr) => {
  if (!startStr || !endStr) return null;
  const diff = new Date(endStr) - new Date(startStr);
  if (diff <= 0) return null;
  return Math.round(diff / 60000);
};

/**
 * Builds the ISO 8601 duration string PT#M for a given number of minutes,
 * e.g. 90 minutes → "PT1H30M".
 */
const toIsoDuration = (minutes) => {
  if (!minutes || minutes <= 0) return undefined;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  let s = "PT";
  if (h) s += `${h}H`;
  if (m) s += `${m}M`;
  return s;
};

// ─────────────────────────────────────────────────────────────────────────────
//  MultiSlotCalendar — full-width Google Calendar week view
//  Manages its own slot list internally; calls onSlotsChange on every update.
//
//  Props:
//    slots           : [{ slotStartDateTime, slotEndDateTime, durationMinutes }]
//    onSlotsChange   : (slots) => void
//    minDateStr      : datetime-local string
//    maxDateStr      : datetime-local string | undefined
// ─────────────────────────────────────────────────────────────────────────────
const HOUR_START = 7;
const HOUR_END = 22;
const SLOT_HEIGHT = 52; // px per hour

const _pad = (n) => String(n).padStart(2, "0");

const _toDatetimeLocal = (date) =>
  `${date.getFullYear()}-${_pad(date.getMonth() + 1)}-${_pad(date.getDate())}T${_pad(date.getHours())}:${_pad(date.getMinutes())}`;

const _fmtTime = (dtStr) =>
  dtStr
    ? new Date(dtStr).toLocaleString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "";

const _durLabel = (mins) => {
  if (!mins) return "";
  const h = Math.floor(mins / 60),
    m = mins % 60;
  return h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`;
};

const SLOT_COLORS = [
  {
    bg: "linear-gradient(160deg,#6366f1,#818cf8)",
    shadow: "rgba(99,102,241,0.35)",
    text: "#fff",
  },
  {
    bg: "linear-gradient(160deg,#0ea5e9,#38bdf8)",
    shadow: "rgba(14,165,233,0.35)",
    text: "#fff",
  },
  {
    bg: "linear-gradient(160deg,#10b981,#34d399)",
    shadow: "rgba(16,185,129,0.35)",
    text: "#fff",
  },
  {
    bg: "linear-gradient(160deg,#f59e0b,#fbbf24)",
    shadow: "rgba(245,158,11,0.35)",
    text: "#fff",
  },
  {
    bg: "linear-gradient(160deg,#ec4899,#f472b6)",
    shadow: "rgba(236,72,153,0.35)",
    text: "#fff",
  },
  {
    bg: "linear-gradient(160deg,#8b5cf6,#a78bfa)",
    shadow: "rgba(139,92,246,0.35)",
    text: "#fff",
  },
];

const DURATION_PILLS = [
  { label: "12 min", minutes: 12 },
  { label: "30 min", minutes: 30 },
  { label: "45 min", minutes: 45 },
  { label: "1 hr", minutes: 60 },
  { label: "Custom", minutes: "custom" },
];

const _isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// True if [aStart,aEnd) overlaps [bStart,bEnd)
const _rangesOverlap = (aStart, aEnd, bStart, bEnd) =>
  aStart < bEnd && bStart < aEnd;

// ── Working-days / holiday blocking helpers ──────────────────────────────────
// yyyy-mm-dd in local time (avoids UTC-shift bugs from toISOString)
const toDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const toMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];
const ORDINALS = ["First", "Second", "Third", "Fourth", "Fifth"];

const nthWeekdayOfMonth = (date) => Math.ceil(date.getDate() / 7);

// Decide whether a given calendar date is blocked, and why, based on the
// employee-monthly attendance response (workingDays config + holidays list).
const getDateBlockInfo = (date, monthMeta, cutoff) => {
  if (cutoff && date < cutoff) {
    return { blocked: true, reason: "Past date" };
  }
  if (!monthMeta) {
    return { blocked: true, reason: "Loading calendar…" };
  }

  const { workingDays, holidays } = monthMeta;
  const dateKey = toDateKey(date);

  const holiday = (holidays || []).find((h) => {
    const start = new Date(h.eventDate);
    const span = h.noOfDays && h.noOfDays > 1 ? h.noOfDays : 1;
    for (let i = 0; i < span; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      if (toDateKey(d) === dateKey) return true;
    }
    return false;
  });
  if (holiday) {
    return { blocked: true, reason: holiday.holidayName || "Holiday" };
  }

  if (workingDays) {
    const dow = date.getDay(); // 0 = Sunday ... 6 = Saturday
    const dayKey = DAY_KEYS[dow];
    const isWorkingByDefault = workingDays[dayKey] !== false;

    if (dow === 6 || dow === 0) {
      const ordinal = ORDINALS[nthWeekdayOfMonth(date) - 1];
      const leaveKey = `leaveOn${ordinal}${dow === 6 ? "Saturday" : "Sunday"}`;
      if (workingDays[leaveKey]) {
        return {
          blocked: true,
          reason: `${ordinal} ${dow === 6 ? "Saturday" : "Sunday"} off`,
        };
      }
    }

    if (!isWorkingByDefault) {
      return { blocked: true, reason: "Weekly off" };
    }
  }

  return { blocked: false, reason: null };
};

const MultiSlotCalendar = ({
  slots,
  onSlotsChange,
  minDateStr,
  maxDateStr,
  existingSlots = [],
}) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // week offset: 0 = week containing today
  const [weekOffset, setWeekOffset] = useState(0);

  // duration state
  const [durationMinutes, setDurationMinutes] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [customVal, setCustomVal] = useState("");

  // hover ghost
  const [hoverInfo, setHoverInfo] = useState(null); // { colIndex, snappedMins }

  // Working-days config + holidays, cached per "yyyy-mm"
  const [monthMetaCache, setMonthMetaCache] = useState({});
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthError, setMonthError] = useState(null);

  // The 7 days of the current week view
  const weekDays = useMemo(() => {
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() + weekOffset * 7);
    // go to Monday of that week
    const dow = startOfWeek.getDay(); // 0=Sun
    const diff = dow === 0 ? -6 : 1 - dow;
    startOfWeek.setDate(startOfWeek.getDate() + diff);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      return d;
    });
  }, [today, weekOffset]);

  // Fetch working-days/holidays for every month touched by the visible week
  useEffect(() => {
    const neededMonthKeys = Array.from(new Set(weekDays.map(toMonthKey)));
    const toFetch = neededMonthKeys.filter((mk) => !(mk in monthMetaCache));
    if (toFetch.length === 0) return undefined;

    let cancelled = false;
    setMonthLoading(true);
    setMonthError(null);

    Promise.all(
      toFetch.map((mk) =>
        axios
          .get(`${ATTENDANCE_MONTHLY_API}?attendanceDate=${mk}-01`)
          .then((res) => ({
            mk,
            workingDays: res.data?.data?.workingDays || null,
            holidays: res.data?.data?.holidays || [],
          }))
          .catch(() => ({ mk, workingDays: null, holidays: [], failed: true })),
      ),
    ).then((results) => {
      if (cancelled) return;
      setMonthMetaCache((prev) => {
        const next = { ...prev };
        results.forEach((r) => {
          next[r.mk] = { workingDays: r.workingDays, holidays: r.holidays };
        });
        return next;
      });
      if (results.some((r) => r.failed)) {
        setMonthError("Couldn't load working-day/holiday data.");
      }
      setMonthLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDays]);

  // Combines the min/max range restriction with working-days/holiday blocking,
  // and returns *why* a day is blocked so the UI can show a tooltip.
  const getDayBlockInfo = (day) => {
    const minD = minDateStr ? new Date(minDateStr.split("T")[0]) : today;
    minD.setHours(0, 0, 0, 0);

    const meta = monthMetaCache[toMonthKey(day)] || null;
    const info = getDateBlockInfo(day, meta, minD);
    if (info.blocked) return info;

    if (maxDateStr) {
      const maxD = new Date(maxDateStr.split("T")[0]);
      maxD.setHours(0, 0, 0, 0);
      if (day > maxD) return { blocked: true, reason: "After closing date" };
    }
    return info;
  };

  const isDayDisabled = (day) => getDayBlockInfo(day).blocked;
  const RANGE_REASONS = [
    "Past date",
    "After closing date",
    "Loading calendar…",
  ];
  const isCompanyOffDay = (day) => {
    const info = getDayBlockInfo(day);
    return info.blocked && !RANGE_REASONS.includes(info.reason);
  };

  const gridHeight = (HOUR_END - HOUR_START) * SLOT_HEIGHT;

  const blockTop = (h, m) => (h - HOUR_START + m / 60) * SLOT_HEIGHT;
  const blockHeight = (mins) => Math.max((mins / 60) * SLOT_HEIGHT, 20);

  // ── Duration pill handler ────────────────────────────────────────────────
  const handleDurationPill = (opt) => {
    if (opt.minutes === "custom") {
      setShowCustom(true);
      return;
    }
    setShowCustom(false);
    setDurationMinutes(opt.minutes);
  };
  const handleCustomApply = () => {
    const v = parseInt(customVal, 10);
    if (!v || v < 1 || v > 480) {
      toast.error("Enter 1–480 minutes.");
      return;
    }
    setDurationMinutes(v);
    setShowCustom(false);
  };

  // ── Column click: add slot ───────────────────────────────────────────────
  const handleColClick = (e, day, colIndex) => {
    if (!durationMinutes) {
      toast.info("Pick a duration first.");
      return;
    }
    if (isDayDisabled(day)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const yRel = e.clientY - rect.top;
    const totalMins = (yRel / SLOT_HEIGHT) * 60 + HOUR_START * 60;
    const snapped = Math.round(totalMins / 15) * 15;
    const startH = Math.floor(snapped / 60);
    const startM = snapped % 60;
    if (startH < HOUR_START || startH >= HOUR_END) return;

    const startDt = new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      startH,
      startM,
    );
    const endDt = new Date(startDt.getTime() + durationMinutes * 60000);

    const clashesExisting = existingSlots.some((s) => {
      if (!s.slotStartDateTime) return false;
      const exStart = new Date(s.slotStartDateTime);
      const exEnd = s.slotEndDateTime
        ? new Date(s.slotEndDateTime)
        : new Date(exStart.getTime() + 30 * 60000);
      return _rangesOverlap(startDt, endDt, exStart, exEnd);
    });
    if (clashesExisting) {
      toast.info("That time is already scheduled. Pick another slot.");
      return;
    }

    const newSlot = {
      slotStartDateTime: _toDatetimeLocal(startDt),
      slotEndDateTime: _toDatetimeLocal(endDt),
      durationMinutes,
    };
    onSlotsChange([...slots, newSlot]);
  };

  // ── Remove slot ──────────────────────────────────────────────────────────
  const removeSlot = (idx) => {
    onSlotsChange(slots.filter((_, i) => i !== idx));
  };

  // ── Hover ghost ──────────────────────────────────────────────────────────
  const handleColMouseMove = (e, day, colIndex) => {
    if (!durationMinutes || isDayDisabled(day)) {
      setHoverInfo(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const yRel = e.clientY - rect.top;
    const totalMins = (yRel / SLOT_HEIGHT) * 60 + HOUR_START * 60;
    const snapped = Math.round(totalMins / 15) * 15;
    const startH = Math.floor(snapped / 60);
    if (startH < HOUR_START || startH >= HOUR_END) {
      setHoverInfo(null);
      return;
    }
    setHoverInfo({ colIndex, snappedMins: snapped });
  };

  const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const MONTHS_SHORT = useMemo(
    () => [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    [],
  );

  // week range label
  const weekLabel = useMemo(() => {
    const a = weekDays[0],
      b = weekDays[6];
    if (a.getMonth() === b.getMonth())
      return `${a.getDate()}–${b.getDate()} ${MONTHS_SHORT[a.getMonth()]} ${a.getFullYear()}`;
    return `${a.getDate()} ${MONTHS_SHORT[a.getMonth()]} – ${b.getDate()} ${MONTHS_SHORT[b.getMonth()]} ${b.getFullYear()}`;
  }, [weekDays, MONTHS_SHORT]);

  const canGoPrev = weekOffset > 0;
  const totalSlots = slots.length;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        width: "100%",
      }}
    >
      {/* ── Duration bar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          padding: "12px 16px",
          background: "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#6b7280",
            flexShrink: 0,
          }}
        >
          Duration:
        </span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {DURATION_PILLS.map((opt) => {
            const isActive =
              opt.minutes !== "custom" && durationMinutes === opt.minutes;
            const isCustActive = opt.minutes === "custom" && showCustom;
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => handleDurationPill(opt)}
                style={{
                  padding: "5px 14px",
                  borderRadius: 20,
                  border: `1.5px solid ${isActive || isCustActive ? "#6366f1" : "#e5e7eb"}`,
                  background: isActive || isCustActive ? "#eef2ff" : "#fff",
                  color: isActive || isCustActive ? "#6366f1" : "#374151",
                  fontSize: 12,
                  fontWeight: isActive || isCustActive ? 700 : 400,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {isActive && (
                  <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2.5 6l2.5 2.5L9.5 3"
                      stroke="#6366f1"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {opt.label}
              </button>
            );
          })}
        </div>
        {showCustom && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="number"
              min={1}
              max={480}
              placeholder="mins"
              value={customVal}
              onChange={(e) => setCustomVal(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCustomApply()}
              style={{
                width: 68,
                padding: "5px 8px",
                borderRadius: 8,
                border: "1.5px solid #6366f1",
                fontSize: 13,
                outline: "none",
              }}
            />
            <button
              type="button"
              onClick={handleCustomApply}
              style={{
                padding: "5px 12px",
                borderRadius: 8,
                border: "none",
                background: "#6366f1",
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Set
            </button>
          </div>
        )}
        {durationMinutes && !showCustom && (
          <span style={{ fontSize: 11, color: "#6366f1", fontWeight: 600 }}>
            ✓ {_durLabel(durationMinutes)}
            {!totalSlots && " · now click a time below"}
          </span>
        )}
        {!durationMinutes && (
          <span style={{ fontSize: 11, color: "#9ca3af" }}>
            ← pick a duration to start booking
          </span>
        )}

        {/* Slot count badge */}
        {totalSlots > 0 && (
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                padding: "3px 10px",
                borderRadius: 20,
                background: "#eef2ff",
                border: "1px solid #c7d2fe",
                fontSize: 12,
                fontWeight: 700,
                color: "#6366f1",
              }}
            >
              {totalSlots} slot{totalSlots > 1 ? "s" : ""} added
            </span>
          </div>
        )}
      </div>

      {/* ── Week nav + grid ── */}
      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
        {/* Week header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px 12px 0",
            gap: 10,
            borderBottom: "1px solid #e5e7eb",
            background: "#fff",
          }}
        >
          <button
            type="button"
            onClick={() => setWeekOffset((o) => o - 1)}
            disabled={!canGoPrev}
            style={{
              border: "1px solid #e5e7eb",
              background: canGoPrev ? "#fff" : "#f9fafb",
              borderRadius: 8,
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: canGoPrev ? "pointer" : "not-allowed",
              color: canGoPrev ? "#374151" : "#d1d5db",
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            ‹
          </button>
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#111827",
              flex: 1,
              textAlign: "center",
            }}
          >
            {weekLabel}
          </span>
          <button
            type="button"
            onClick={() => setWeekOffset((o) => o + 1)}
            style={{
              border: "1px solid #e5e7eb",
              background: "#fff",
              borderRadius: 8,
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#374151",
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            ›
          </button>
        </div>

        {(monthLoading || monthError) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 12px",
              fontSize: 11,
              color: monthError ? "#dc2626" : "#9ca3af",
            }}
          >
            {monthLoading && !monthError && "Checking working days & holidays…"}
            {monthError}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "4px 12px 6px",
            fontSize: 10.5,
            color: "#6b7280",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: 2,
                background: "#fef2f2",
                border: "1px solid #fecaca",
                display: "inline-block",
              }}
            />
            Holiday / weekly off
          </span>
          {existingSlots.length > 0 && (
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 2,
                  background:
                    "repeating-linear-gradient(45deg,#e5e7eb,#e5e7eb 3px,#f3f4f6 3px,#f3f4f6 6px)",
                  border: "1px solid #d1d5db",
                  display: "inline-block",
                }}
              />
              Existing slot (already scheduled)
            </span>
          )}
        </div>

        {/* Day header row */}
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #e5e7eb",
            background: "#fff",
            position: "sticky",
            top: 0,
            zIndex: 3,
          }}
        >
          <div style={{ width: 48, flexShrink: 0 }} />
          {weekDays.map((day, ci) => {
            const blockInfo = getDayBlockInfo(day);
            const disabled = blockInfo.blocked;
            const isOff = isCompanyOffDay(day);
            const isToday = _isSameDay(day, today);
            const daySlots = slots.filter(
              (s) =>
                s.slotStartDateTime &&
                _isSameDay(new Date(s.slotStartDateTime), day),
            );
            return (
              <div
                key={ci}
                title={blockInfo.reason || undefined}
                style={{
                  flex: 1,
                  padding: "6px 4px",
                  textAlign: "center",
                  opacity: disabled ? 0.4 : 1,
                  background: isOff ? "#fef2f2" : "transparent",
                  borderRadius: isOff ? 6 : 0,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: isOff ? "#dc2626" : "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {DAY_LABELS[ci]}
                </div>
                <div
                  style={{
                    width: 30,
                    height: 30,
                    margin: "2px auto 0",
                    borderRadius: "50%",
                    background: isToday ? "#6366f1" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: isToday ? "#fff" : "#111827",
                  }}
                >
                  {day.getDate()}
                </div>
                {daySlots.length > 0 && (
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#6366f1",
                      margin: "3px auto 0",
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        <div
          style={{
            display: "flex",
            overflowY: "auto",
            maxHeight: 440,
            position: "relative",
          }}
        >
          {/* Hour labels */}
          <div
            style={{
              width: 48,
              flexShrink: 0,
              position: "sticky",
              left: 0,
              background: "#fff",
              zIndex: 2,
            }}
          >
            {Array.from({ length: HOUR_END - HOUR_START }, (_, i) => {
              const h = HOUR_START + i;
              const label =
                h === 0
                  ? "12 AM"
                  : h < 12
                    ? `${h} AM`
                    : h === 12
                      ? "12 PM"
                      : `${h - 12} PM`;
              return (
                <div
                  key={h}
                  style={{
                    height: SLOT_HEIGHT,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-end",
                    paddingRight: 8,
                    paddingTop: 4,
                    fontSize: 10,
                    color: "#9ca3af",
                    fontWeight: 500,
                    boxSizing: "border-box",
                    borderTop: "1px solid #f3f4f6",
                  }}
                >
                  {label}
                </div>
              );
            })}
          </div>

          {/* Day columns */}
          {weekDays.map((day, ci) => {
            const blockInfo = getDayBlockInfo(day);
            const disabled = blockInfo.blocked;
            const isOff = isCompanyOffDay(day);
            const isToday = _isSameDay(day, today);
            const daySlots = slots.reduce((acc, s, idx) => {
              if (
                s.slotStartDateTime &&
                _isSameDay(new Date(s.slotStartDateTime), day)
              )
                acc.push({ ...s, _idx: idx });
              return acc;
            }, []);
            const existingDaySlots = existingSlots.filter(
              (s) =>
                s.slotStartDateTime &&
                _isSameDay(new Date(s.slotStartDateTime), day),
            );
            const showGhost =
              hoverInfo &&
              hoverInfo.colIndex === ci &&
              durationMinutes &&
              !disabled;
            const ghostH = showGhost
              ? Math.floor(hoverInfo.snappedMins / 60)
              : 0;
            const ghostM = showGhost ? hoverInfo.snappedMins % 60 : 0;

            return (
              <div
                key={ci}
                title={blockInfo.reason || undefined}
                style={{
                  flex: 1,
                  position: "relative",
                  borderLeft: `1px solid ${isToday ? "#e0e7ff" : "#f3f4f6"}`,
                  background: isToday
                    ? "#fafafe"
                    : isOff
                      ? "#fef2f2"
                      : disabled
                        ? "#fafafa"
                        : "#fff",
                  cursor: disabled
                    ? "not-allowed"
                    : durationMinutes
                      ? "crosshair"
                      : "default",
                  minWidth: 0,
                }}
                onClick={(e) => !disabled && handleColClick(e, day, ci)}
                onMouseMove={(e) => handleColMouseMove(e, day, ci)}
                onMouseLeave={() => setHoverInfo(null)}
              >
                {/* Hour grid lines */}
                {Array.from({ length: HOUR_END - HOUR_START }, (_, i) => (
                  <div
                    key={i}
                    style={{
                      position: "absolute",
                      top: i * SLOT_HEIGHT,
                      left: 0,
                      right: 0,
                      borderTop: "1px solid #f3f4f6",
                      height: SLOT_HEIGHT,
                      boxSizing: "border-box",
                      pointerEvents: "none",
                    }}
                  />
                ))}
                {/* Half-hour dashes */}
                {Array.from({ length: HOUR_END - HOUR_START }, (_, i) => (
                  <div
                    key={`h${i}`}
                    style={{
                      position: "absolute",
                      top: i * SLOT_HEIGHT + SLOT_HEIGHT / 2,
                      left: 4,
                      right: 0,
                      borderTop: "1px dashed #f0f0f0",
                      pointerEvents: "none",
                    }}
                  />
                ))}

                {/* Existing (already-scheduled) slots — read-only */}
                {existingDaySlots.map((s, ei) => {
                  const exStart = new Date(s.slotStartDateTime);
                  const h = exStart.getHours(),
                    m = exStart.getMinutes();
                  const exMins = s.slotEndDateTime
                    ? Math.max(
                        Math.round(
                          (new Date(s.slotEndDateTime) - exStart) / 60000,
                        ),
                        1,
                      )
                    : 30;
                  const statusMeta =
                    SLOT_STATUS_BADGE[s.slotStatus] || SLOT_STATUS_BADGE.BOOKED;
                  return (
                    <div
                      key={`existing-${ei}`}
                      title={`Already scheduled${s.slotStatus ? ` · ${statusMeta.label}` : ""} · ${_fmtTime(s.slotStartDateTime)}`}
                      style={{
                        position: "absolute",
                        top: blockTop(h, m) + 1,
                        left: 3,
                        right: 3,
                        height: blockHeight(exMins) - 2,
                        borderRadius: 6,
                        background:
                          "repeating-linear-gradient(45deg,#e5e7eb,#e5e7eb 4px,#f3f4f6 4px,#f3f4f6 8px)",
                        border: `1px solid ${statusMeta.color}55`,
                        zIndex: 1,
                        pointerEvents: "none",
                        padding: "3px 6px",
                        overflow: "hidden",
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: statusMeta.color,
                          lineHeight: 1.3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {_fmtTime(s.slotStartDateTime)} · {statusMeta.label}
                      </div>
                    </div>
                  );
                })}

                {/* Ghost hover block */}
                {showGhost && (
                  <div
                    style={{
                      position: "absolute",
                      top: blockTop(ghostH, ghostM),
                      left: 3,
                      right: 3,
                      height: blockHeight(durationMinutes),
                      background: "rgba(99,102,241,0.18)",
                      border: "1.5px dashed #6366f1",
                      borderRadius: 6,
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Slot blocks */}
                {daySlots.map((s, si) => {
                  const startDt = new Date(s.slotStartDateTime);
                  const color = SLOT_COLORS[s._idx % SLOT_COLORS.length];
                  const h = startDt.getHours(),
                    m = startDt.getMinutes();
                  const slotMins =
                    s.durationMinutes ||
                    (s.slotEndDateTime
                      ? Math.round(
                          (new Date(s.slotEndDateTime) - startDt) / 60000,
                        )
                      : 30);
                  return (
                    <div
                      key={si}
                      style={{
                        position: "absolute",
                        top: blockTop(h, m) + 1,
                        left: 3,
                        right: 3,
                        height: blockHeight(slotMins) - 2,
                        background: color.bg,
                        borderRadius: 6,
                        boxShadow: `0 2px 8px ${color.shadow}`,
                        padding: "3px 6px",
                        zIndex: 2,
                        cursor: "pointer",
                        overflow: "hidden",
                        userSelect: "none",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSlot(s._idx);
                      }}
                      title="Click to remove"
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: color.text,
                          lineHeight: 1.3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {_fmtTime(s.slotStartDateTime)}
                      </div>
                      {slotMins >= 25 && (
                        <div
                          style={{
                            fontSize: 9,
                            color: "rgba(255,255,255,0.85)",
                            marginTop: 1,
                          }}
                        >
                          {_durLabel(slotMins)} · tap to remove
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Invisible full-height hit target */}
                <div style={{ height: gridHeight, width: "100%" }} />
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Slot list summary ── */}
      {slots.length > 0 && (
        <div
          style={{
            padding: "12px 16px",
            borderTop: "1px solid #e5e7eb",
            background: "#f8fafc",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 8,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Booked slots
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {slots.map((s, i) => {
              const color = SLOT_COLORS[i % SLOT_COLORS.length];
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                    borderRadius: 8,
                    overflow: "hidden",
                    border: "1px solid #e5e7eb",
                    fontSize: 11,
                  }}
                >
                  <div
                    style={{
                      width: 4,
                      alignSelf: "stretch",
                      background: color.bg,
                    }}
                  />
                  <div
                    style={{
                      padding: "4px 10px",
                      background: "#fff",
                      color: "#374151",
                      fontWeight: 500,
                    }}
                  >
                    {s.slotStartDateTime
                      ? new Date(s.slotStartDateTime).toLocaleDateString(
                          "en-IN",
                          { weekday: "short", day: "2-digit", month: "short" },
                        )
                      : "—"}{" "}
                    · {_fmtTime(s.slotStartDateTime)} –{" "}
                    {_fmtTime(s.slotEndDateTime)}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSlot(i)}
                    style={{
                      padding: "4px 8px",
                      border: "none",
                      background: "#fef2f2",
                      color: "#ef4444",
                      cursor: "pointer",
                      fontSize: 13,
                      lineHeight: 1,
                      borderLeft: "1px solid #fee2e2",
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Shared Job Post Filter Bar
// ─────────────────────────────────────────────────────────────────────────────
const JobPostFilterBar = ({
  jobs,
  selected,
  onSelect,
  totalCount,
  filteredCount,
}) => {
  if (jobs.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        padding: "10px 14px",
        background: "#f8fafc",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          fontSize: 12,
          fontWeight: 600,
          color: "#6b7280",
          marginRight: 4,
          flexShrink: 0,
        }}
      >
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
          <rect
            x="1"
            y="4"
            width="12"
            height="8"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="1.3"
          />
          <path
            d="M4 4V3a3 3 0 016 0v1"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
        Job Post:
      </div>
      <button
        onClick={() => onSelect("")}
        style={{
          padding: "4px 12px",
          borderRadius: 20,
          border: `1.5px solid ${!selected ? "#6366f1" : "#e5e7eb"}`,
          background: !selected ? "#eef2ff" : "#fff",
          color: !selected ? "#6366f1" : "#6b7280",
          fontSize: 12,
          fontWeight: !selected ? 700 : 400,
          cursor: "pointer",
          transition: "all 0.15s",
        }}
      >
        All Jobs
      </button>
      {jobs.map((j) => {
        const isActive = selected === String(j.id);
        return (
          <button
            key={j.id}
            onClick={() => onSelect(isActive ? "" : String(j.id))}
            style={{
              padding: "4px 12px",
              borderRadius: 20,
              border: `1.5px solid ${isActive ? "#6366f1" : "#e5e7eb"}`,
              background: isActive ? "#eef2ff" : "#fff",
              color: isActive ? "#6366f1" : "#374151",
              fontSize: 12,
              fontWeight: isActive ? 700 : 400,
              cursor: "pointer",
              transition: "all 0.15s",
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            {isActive && (
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                <path
                  d="M2.5 6l2.5 2.5L9.5 3"
                  stroke="#6366f1"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {j.title}
          </button>
        );
      })}
      {selected && totalCount != null && (
        <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>
          Showing {filteredCount} of {totalCount}
        </span>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Next Round URL Popup Modal
// ─────────────────────────────────────────────────────────────────────────────
const NextRoundUrlModal = ({ data, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(data.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div
        className="drawer"
        style={{ maxWidth: 480, borderTop: "3px solid #16a34a" }}
      >
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title" style={{ color: "#16a34a" }}>
              🎉 Candidate Shortlisted!
            </h2>
            <p className="drawer__sub">
              Interview moved to{" "}
              <strong>{data.newInterviewRoundName || "next round"}</strong>
            </p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="drawer__body">
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 20,
            }}
          >
            {data.interviewerName && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 10px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 20,
                  fontSize: 12,
                  color: "#166534",
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: avatarColor(data.interviewerName),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 700,
                  }}
                >
                  {getInitials(data.interviewerName)}
                </div>
                {data.interviewerName}
              </span>
            )}
            {data.roundName && (
              <span
                style={{
                  padding: "5px 10px",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: 20,
                  fontSize: 12,
                  color: "#1e40af",
                }}
              >
                📋 {data.roundName}
              </span>
            )}
            {data.status && (
              <span
                style={{
                  padding: "5px 10px",
                  background: "#dcfce7",
                  border: "1px solid #86efac",
                  borderRadius: 20,
                  fontSize: 12,
                  color: "#15803d",
                  fontWeight: 600,
                }}
              >
                ✓ {data.status}
              </span>
            )}
          </div>
          {data.url && (
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: 6,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Next Round Selection URL
              </label>
              <p
                style={{
                  fontSize: 12,
                  color: "#6b7280",
                  marginBottom: 10,
                  lineHeight: 1.5,
                }}
              >
                Share this link with the candidate so they can select a slot for
                the next interview round.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "10px 12px",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{ flexShrink: 0, color: "#6366f1" }}
                >
                  <path
                    d="M6.5 9.5a3.5 3.5 0 0 0 4.95 0l2-2a3.5 3.5 0 0 0-4.95-4.95l-1 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M9.5 6.5a3.5 3.5 0 0 0-4.95 0l-2 2a3.5 3.5 0 0 0 4.95 4.95l1-1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <span
                  style={{
                    flex: 1,
                    fontSize: 12,
                    color: "#6366f1",
                    wordBreak: "break-all",
                    fontFamily: "monospace",
                  }}
                >
                  {data.url}
                </span>
                <button
                  onClick={handleCopy}
                  style={{
                    flexShrink: 0,
                    padding: "4px 10px",
                    background: copied ? "#16a34a" : "#6366f1",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "background 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  {copied ? (
                    <>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 6l3 3 5-5"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <rect
                          x="4"
                          y="4"
                          width="7"
                          height="7"
                          rx="1"
                          stroke="currentColor"
                          strokeWidth="1.2"
                        />
                        <path
                          d="M3 8H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1v1"
                          stroke="currentColor"
                          strokeWidth="1.2"
                        />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
              <a
                href={data.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  marginTop: 10,
                  fontSize: 12,
                  color: "#6366f1",
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 6h8M6 2l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Open in new tab
              </a>
            </div>
          )}
          {data.message && (
            <div
              style={{
                marginTop: 16,
                padding: "10px 14px",
                background: "#fafafa",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                fontSize: 12,
                color: "#6b7280",
                fontStyle: "italic",
              }}
            >
              {data.message}
            </div>
          )}
        </div>
        <div className="drawer__footer">
          <div style={{ flex: 1 }} />
          <button
            className="cv-btn cv-btn--submit"
            onClick={onClose}
            style={{ background: "#16a34a" }}
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Cancel Slot Button
// ─────────────────────────────────────────────────────────────────────────────
const CancelSlotButton = ({
  slot,
  scheduledRoundId,
  onRefresh,
  onCancelledBooked,
}) => {
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [remark, setRemark] = useState("");
  const [remarkError, setRemarkError] = useState(false);

  const isBooked = (slot.slotStatus || "").toUpperCase() === "BOOKED";

  const doCancel = async () => {
    if (isBooked && !remark.trim()) {
      setRemarkError(true);
      return;
    }

    setShowConfirm(false);
    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/interview-schedules/cancel`,
        {
          scheduledRoundId,
          oldSlotId: slot.id,
          action: "CANCELLED",
          comment: isBooked ? remark.trim() : undefined,
        },
      );
      toast.success("Slot cancelled.");
      onRefresh();
      if (isBooked && onCancelledBooked) onCancelledBooked();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to cancel slot.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setShowConfirm(true);
  };

  return (
    <>
      <button
        onClick={handleOpen}
        disabled={loading}
        style={{
          padding: "4px 11px",
          borderRadius: 20,
          border: "1.5px solid #dc2626",
          background: "#fff",
          color: "#dc2626",
          fontSize: 11,
          fontWeight: 500,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 4,
          transition: "all 0.15s",
        }}
      >
        {loading ? "Cancelling…" : "Cancel"}
      </button>

      {showConfirm && (
        <>
          <div
            className="drawer-backdrop"
            style={{ zIndex: 1100 }}
            onClick={() => setShowConfirm(false)}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1200,
              background: "#fff",
              borderRadius: 14,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              padding: "28px 28px 22px",
              maxWidth: 420,
              width: "90vw",
              borderTop: "4px solid #dc2626",
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 14,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 6v5M10 13.5v.5"
                  stroke="#dc2626"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle
                  cx="10"
                  cy="10"
                  r="8.5"
                  stroke="#dc2626"
                  strokeWidth="1.5"
                />
              </svg>
            </div>

            <h3
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#111827",
                margin: "0 0 6px",
              }}
            >
              {isBooked ? "Cancel Booked Slot?" : "Cancel Available Slot?"}
            </h3>
            <p
              style={{
                fontSize: 13,
                color: "#6b7280",
                margin: "0 0 16px",
                lineHeight: 1.55,
              }}
            >
              {isBooked
                ? "This slot already has a candidate assigned. Cancelling it will unschedule the candidate from this time window."
                : "This slot will be removed from your availability."}
            </p>

            <div
              style={{
                display: "flex",
                gap: 12,
                padding: "10px 12px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                marginBottom: 16,
                fontSize: 12,
              }}
            >
              <div>
                <div style={{ color: "#9ca3af", marginBottom: 2 }}>Start</div>
                <div style={{ fontWeight: 600, color: "#991b1b" }}>
                  {fmt(slot.slotStartDateTime)}
                </div>
              </div>
              <div
                style={{ borderLeft: "1px solid #fecaca", margin: "0 4px" }}
              />
              <div>
                <div style={{ color: "#9ca3af", marginBottom: 2 }}>End</div>
                <div style={{ fontWeight: 600, color: "#991b1b" }}>
                  {fmt(slot.slotEndDateTime)}
                </div>
              </div>
            </div>

            {isBooked && (
              <div style={{ marginBottom: 18 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: 6,
                  }}
                >
                  Reason for cancellation{" "}
                  <span style={{ color: "#dc2626" }}>*</span>
                </label>

                <textarea
                  rows={3}
                  placeholder="Please provide a reason for cancelling this booked slot…"
                  value={remark}
                  onChange={(e) => {
                    setRemark(e.target.value);

                    if (e.target.value.trim()) {
                      setRemarkError(false);
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "9px 11px",
                    borderRadius: 8,
                    border: `1.5px solid ${
                      remarkError ? "#dc2626" : "#e5e7eb"
                    }`,
                    fontSize: 13,
                    color: "#111827",
                    resize: "vertical",
                    minHeight: 72,
                    outline: "none",
                    boxSizing: "border-box",
                    background: remarkError ? "#fff5f5" : "#fff",
                  }}
                />

                {remarkError && (
                  <p
                    style={{
                      fontSize: 11,
                      color: "#dc2626",
                      marginTop: 4,
                    }}
                  >
                    Reason is required for booked slot cancellation.
                  </p>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => {
                  setShowConfirm(false);
                }}
                style={{
                  flex: 1,
                  padding: "9px 0",
                  borderRadius: 8,
                  border: "1.5px solid #e5e7eb",
                  background: "#fff",
                  color: "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Keep Slot
              </button>
              <button
                onClick={doCancel}
                style={{
                  flex: 1,
                  padding: "9px 0",
                  borderRadius: 8,
                  border: "none",
                  background: "#dc2626",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Main Page
// ─────────────────────────────────────────────────────────────────────────────
const ScheduleInterviewPage = () => {
  const { user } = useAuth();
  const empId = user?.empId ?? null;

  const [activeTab, setActiveTab] = useState("slots");

  // Slots tab state
  const [rounds, setRounds] = useState([]);
  const [roundsLoading, setRoundsLoading] = useState(true);
  const [selectedRound, setSelectedRound] = useState(null);
  const [roundDetail, setRoundDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [addSlotOpen, setAddSlotOpen] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [search, setSearch] = useState("");
  const [slotsJobFilter, setSlotsJobFilter] = useState("");

  // Interviews tab state
  const [interviews, setInterviews] = useState([]);
  const [interviewsLoading, setInterviewsLoading] = useState(false);
  const [updateStatusTarget, setUpdateStatusTarget] = useState(null);
  const [nextRoundData, setNextRoundData] = useState(null);

  const fetchRounds = useCallback(() => {
    if (!empId) return;
    setRoundsLoading(true);
    axios
      .get(`${EMP_SCHEDULE_API}/employee/${empId}`)
      .then((r) => {
        const list = r.data.data || [];
        setRounds(list);
        if (list.length > 0) setSelectedRound(list[0]);
      })
      .catch(() => toast.error("Failed to load your interview rounds."))
      .finally(() => setRoundsLoading(false));
  }, [empId]);

  useEffect(() => {
    fetchRounds();
  }, [fetchRounds]);

  const fetchRoundDetail = useCallback(() => {
    if (!selectedRound?.id) return;
    setDetailLoading(true);
    setRoundDetail(null);
    axios
      .get(`${EMP_SCHEDULE_API}/${selectedRound.id}`)
      .then((r) => setRoundDetail(r.data.data || null))
      .catch(() => toast.error("Failed to load slot details."))
      .finally(() => setDetailLoading(false));
  }, [selectedRound]);

  useEffect(() => {
    fetchRoundDetail();
  }, [fetchRoundDetail]);

  const fetchInterviews = useCallback(() => {
    if (!empId) return;
    setInterviewsLoading(true);
    axios
      .get(`${EMP_INTERVIEW_HISTORY_API}/interviewer/${empId}`)
      .then((r) => setInterviews(r.data.data || []))
      .catch(() => toast.error("Failed to load your interviews."))
      .finally(() => setInterviewsLoading(false));
  }, [empId]);

  useEffect(() => {
    if (activeTab === "interviews") fetchInterviews();
  }, [activeTab, fetchInterviews]);

  const slotsJobOptions = useMemo(() => {
    const map = new Map();
    rounds.forEach((r) => {
      if (r.jobPostId && r.jobTitle) map.set(String(r.jobPostId), r.jobTitle);
    });
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [rounds]);

  const filteredRounds = rounds.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      r.roundName?.toLowerCase().includes(q) ||
      r.jobTitle?.toLowerCase().includes(q);
    const matchJob = !slotsJobFilter || String(r.jobPostId) === slotsJobFilter;
    return matchSearch && matchJob;
  });

  const totalSlots = roundDetail?.slots?.length || 0;
  const blockedSlots =
    roundDetail?.slots?.filter((s) => s.slotStatus === "BLOCKED").length || 0;
  const availSlots =
    roundDetail?.slots?.filter((s) => s.slotStatus === "AVAILABLE").length || 0;

  const isJobExpired = useMemo(() => {
    const closingDate =
      roundDetail?.jobPostClosingDateTime ||
      selectedRound?.jobPostClosingDateTime;
    if (!closingDate) return false;
    return new Date(closingDate) < new Date();
  }, [roundDetail, selectedRound]);

  if (!empId) {
    return (
      <div className="cand-empty">
        <span className="cand-empty__icon">⚠️</span>
        <p>
          Could not determine your employee ID. Please check your login session.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="si-inner-tabs">
        <button
          className={`si-inner-tab${activeTab === "slots" ? " si-inner-tab--active" : ""}`}
          onClick={() => setActiveTab("slots")}
        >
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <rect
              x="1"
              y="2"
              width="12"
              height="11"
              rx="1.5"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <path
              d="M4 1v2M10 1v2M1 5.5h12"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
            <path
              d="M4 8h2M4 10h4"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
          Manage Slots
        </button>
        <button
          className={`si-inner-tab${activeTab === "interviews" ? " si-inner-tab--active" : ""}`}
          onClick={() => setActiveTab("interviews")}
        >
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <circle
              cx="5"
              cy="4"
              r="2.5"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <path
              d="M1 12c0-2.21 1.79-4 4-4s4 1.79 4 4"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
            <path
              d="M10 6l1.5 1.5L13 5"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          My Interviews
        </button>
      </div>

      {/* ── SLOTS TAB ── */}
      {activeTab === "slots" && (
        <div className="si-root">
          <aside className="si-rail">
            <div className="si-rail__head">
              <div>
                <span className="si-rail__title">My Interview Rounds</span>
                <span className="si-rail__hint">
                  Candidates requesting your time
                </span>
              </div>
              <span className="si-rail__count">{rounds.length}</span>
            </div>

            {slotsJobOptions.length > 0 && (
              <div
                style={{
                  padding: "8px 12px",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: 6,
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                    <rect
                      x="1"
                      y="4"
                      width="12"
                      height="8"
                      rx="1.5"
                      stroke="currentColor"
                      strokeWidth="1.3"
                    />
                    <path
                      d="M4 4V3a3 3 0 016 0v1"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                  </svg>
                  Filter by Job Post
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  <button
                    onClick={() => setSlotsJobFilter("")}
                    style={{
                      padding: "3px 10px",
                      borderRadius: 20,
                      border: `1.5px solid ${!slotsJobFilter ? "#6366f1" : "#e5e7eb"}`,
                      background: !slotsJobFilter ? "#eef2ff" : "#fff",
                      color: !slotsJobFilter ? "#6366f1" : "#6b7280",
                      fontSize: 11,
                      fontWeight: !slotsJobFilter ? 700 : 400,
                      cursor: "pointer",
                    }}
                  >
                    All
                  </button>
                  {slotsJobOptions.map((j) => {
                    const isActive = slotsJobFilter === String(j.id);
                    return (
                      <button
                        key={j.id}
                        onClick={() =>
                          setSlotsJobFilter(isActive ? "" : String(j.id))
                        }
                        style={{
                          padding: "3px 10px",
                          borderRadius: 20,
                          border: `1.5px solid ${isActive ? "#6366f1" : "#e5e7eb"}`,
                          background: isActive ? "#eef2ff" : "#fff",
                          color: isActive ? "#6366f1" : "#374151",
                          fontSize: 11,
                          fontWeight: isActive ? 700 : 400,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {isActive && (
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <path
                              d="M2.5 6l2.5 2.5L9.5 3"
                              stroke="#6366f1"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                        {j.title}
                      </button>
                    );
                  })}
                </div>
                {slotsJobFilter && (
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 5 }}>
                    {filteredRounds.length} of {rounds.length} rounds
                  </div>
                )}
              </div>
            )}

            <div className="si-rail-search-wrap">
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <circle
                  cx="6"
                  cy="6"
                  r="4.5"
                  stroke="#9ca3af"
                  strokeWidth="1.4"
                />
                <path
                  d="M9.5 9.5l2.5 2.5"
                  stroke="#9ca3af"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
              <input
                className="si-rail-search"
                placeholder="Search by job or round..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  className="cand-search-clear"
                  onClick={() => setSearch("")}
                >
                  ×
                </button>
              )}
            </div>

            {roundsLoading ? (
              <div className="si-rail__loading">Loading your rounds…</div>
            ) : filteredRounds.length === 0 ? (
              <div className="si-rail__empty">
                {search || slotsJobFilter
                  ? "No rounds match your filters."
                  : "No interview rounds assigned to you yet."}
              </div>
            ) : (
              <div className="si-rail__list">
                {filteredRounds.map((r) => {
                  const isActive = selectedRound?.id === r.id;
                  const slotCount = r.slots?.length || 0;
                  const blocked =
                    r.slots?.filter((s) => s.slotStatus === "BLOCKED").length ||
                    0;
                  const hasAvail = r.slots?.some(
                    (s) => s.slotStatus === "AVAILABLE",
                  );
                  return (
                    <button
                      key={r.id}
                      className={`si-round-card${isActive ? " si-round-card--active" : ""}`}
                      onClick={() => setSelectedRound(r)}
                    >
                      <div className="si-round-card__job">
                        {r.jobTitle || "—"}
                      </div>
                      <div className="si-round-card__top">
                        <span className="si-round-card__name">
                          {r.roundName || "Round"}
                        </span>
                        {r.interviewMode && (
                          <span className="si-round-mode-chip">
                            {r.interviewMode}
                          </span>
                        )}
                      </div>
                      <div className="si-round-card__meta">
                        <span className="si-round-card__slots">
                          {slotCount === 0
                            ? "No slots added"
                            : `${blocked}/${slotCount} blocked`}
                        </span>
                        <span
                          className={`si-round-avail-dot${hasAvail ? " si-round-avail-dot--yes" : " si-round-avail-dot--no"}`}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </aside>

          <div className="si-detail">
            {!selectedRound ? (
              <div className="si-detail__empty">
                <div className="si-detail__empty-icon">📅</div>
                <p>
                  Select a round on the left to manage your availability slots.
                </p>
              </div>
            ) : (
              <>
                <div className="si-detail__head">
                  <div className="si-detail__head-left">
                    <div className="si-cand-hero">
                      <div
                        className="si-cand-avatar"
                        style={{
                          background: avatarColor(
                            roundDetail?.interviewerName || "",
                          ),
                        }}
                      >
                        {getInitials(
                          roundDetail?.interviewerName ||
                            selectedRound.interviewerName ||
                            "",
                        )}
                      </div>
                      <div>
                        <div className="si-cand-name">
                          {roundDetail?.interviewerName ||
                            selectedRound.interviewerName ||
                            "—"}
                        </div>
                        <div className="si-cand-sub">
                          Interviewer for this round
                        </div>
                      </div>
                    </div>
                    <div className="si-detail__round-meta">
                      <span className="si-meta-chip">
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <rect
                            x="1"
                            y="2"
                            width="10"
                            height="8"
                            rx="1.2"
                            stroke="currentColor"
                            strokeWidth="1.2"
                          />
                          <path
                            d="M4 2V1M8 2V1M1 5h10"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                          />
                        </svg>
                        {roundDetail?.roundName ||
                          selectedRound.roundName ||
                          "—"}
                      </span>
                      <span className="si-meta-chip">
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <rect
                            x="1"
                            y="3"
                            width="10"
                            height="7"
                            rx="1.2"
                            stroke="currentColor"
                            strokeWidth="1.2"
                          />
                          <path
                            d="M4 3V2a2 2 0 014 0v1"
                            stroke="currentColor"
                            strokeWidth="1.2"
                          />
                        </svg>
                        {roundDetail?.jobTitle || selectedRound.jobTitle || "—"}
                      </span>
                      {(roundDetail?.interviewMode ||
                        selectedRound.interviewMode) && (
                        <span className="si-meta-chip si-meta-chip--mode">
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <circle
                              cx="6"
                              cy="6"
                              r="4.5"
                              stroke="currentColor"
                              strokeWidth="1.2"
                            />
                            <path
                              d="M6 3.5v2.8l1.5 1"
                              stroke="currentColor"
                              strokeWidth="1.2"
                              strokeLinecap="round"
                            />
                          </svg>
                          {roundDetail?.interviewMode ||
                            selectedRound.interviewMode}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="si-detail__head-right">
                    <div className="si-slot-stats">
                      <div className="si-slot-stat">
                        <span className="si-slot-stat__val">{totalSlots}</span>
                        <span className="si-slot-stat__label">Total</span>
                      </div>
                      <div className="si-slot-stat si-slot-stat--avail">
                        <span className="si-slot-stat__val">{availSlots}</span>
                        <span className="si-slot-stat__label">Available</span>
                      </div>
                      <div className="si-slot-stat si-slot-stat--booked">
                        <span className="si-slot-stat__val">
                          {blockedSlots}
                        </span>
                        <span className="si-slot-stat__label">Blocked</span>
                      </div>
                    </div>
                    {roundDetail?.status === "ACTIVE" && (
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <button
                          className="si-add-slot-btn"
                          onClick={() => !isJobExpired && setAddSlotOpen(true)}
                          disabled={isJobExpired}
                          style={{
                            opacity: isJobExpired ? 0.5 : 1,
                            cursor: isJobExpired ? "not-allowed" : "pointer",
                            pointerEvents: isJobExpired ? "none" : "auto",
                          }}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <path
                              d="M7 2v10M2 7h10"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                          Add Slot
                        </button>
                        {isJobExpired && (
                          <div className="si-expired-tooltip">
                            Job posting has expired
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {detailLoading ? (
                  <div className="si-detail__loading">Loading slots…</div>
                ) : !roundDetail?.slots?.length ? (
                  <div className="si-slots-empty">
                    <span style={{ fontSize: 36 }}>🕐</span>
                    <p>
                      You haven't added any availability slots for this round
                      yet.
                    </p>
                    <p className="si-slots-empty__hint">
                      Add slots so candidates can be scheduled into your
                      available windows.
                    </p>
                    {roundDetail?.status === "ACTIVE" && (
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <button
                          className="si-add-slot-btn"
                          onClick={() => !isJobExpired && setAddSlotOpen(true)}
                          disabled={isJobExpired}
                          style={{
                            opacity: isJobExpired ? 0.5 : 1,
                            cursor: isJobExpired ? "not-allowed" : "pointer",
                            pointerEvents: isJobExpired ? "none" : "auto",
                          }}
                        >
                          Add your first slot
                        </button>
                        {isJobExpired && (
                          <div className="si-expired-tooltip">
                            Job posting has expired
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="si-slots-grid">
                    {roundDetail.slots.map((slot) => {
                      const status = slot.slotStatus?.toUpperCase();
                      const sm = SLOT_STATUS_BADGE[status] || {
                        label: slot.slotStatus,
                        color: "#6b7280",
                        bg: "#f3f4f6",
                      };
                      return (
                        <div key={slot.id} className="si-slot-card">
                          <div className="si-slot-card__top">
                            <span
                              className="si-slot-status"
                              style={{ color: sm.color, background: sm.bg }}
                            >
                              {sm.label || slot.slotStatus}
                            </span>

                            {slot.isScheduled && (
                              <span className="si-slot-booked-pill">
                                <svg
                                  width="10"
                                  height="10"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                >
                                  <circle
                                    cx="6"
                                    cy="6"
                                    r="5"
                                    stroke="#1d4ed8"
                                    strokeWidth="1.2"
                                  />
                                  <path
                                    d="M3.5 6l1.8 1.8L8.5 4"
                                    stroke="#1d4ed8"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                Candidate Assigned
                              </span>
                            )}
                          </div>

                          <div className="si-slot-time">
                            <div className="si-slot-time__block">
                              <span className="si-slot-time__label">Start</span>
                              <span className="si-slot-time__val">
                                {fmt(slot.slotStartDateTime)}
                              </span>
                            </div>
                            <div className="si-slot-time__divider">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M3 8h10M9 4l4 4-4 4"
                                  stroke="#d1d5db"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <div className="si-slot-time__block">
                              <span className="si-slot-time__label">End</span>
                              <span className="si-slot-time__val">
                                {fmt(slot.slotEndDateTime)}
                              </span>
                            </div>
                          </div>

                          {slot.interviewerName && (
                            <div className="si-slot-interviewer">
                              <div
                                className="si-slot-avatar"
                                style={{
                                  background: avatarColor(slot.interviewerName),
                                }}
                              >
                                {getInitials(slot.interviewerName)}
                              </div>
                              <span>{slot.interviewerName}</span>
                            </div>
                          )}

                          <div className="si-slot-actions">
                            {(status === "AVAILABLE" ||
                              status === "BOOKED") && (
                              <CancelSlotButton
                                slot={slot}
                                scheduledRoundId={roundDetail?.id}
                                onRefresh={fetchRoundDetail}
                                onCancelledBooked={() => setAddSlotOpen(true)}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── INTERVIEWS TAB ── */}
      {activeTab === "interviews" && (
        <MyInterviewsTab
          interviews={interviews}
          loading={interviewsLoading}
          onRefresh={fetchInterviews}
          onUpdateStatus={(interview) => setUpdateStatusTarget(interview)}
        />
      )}

      {/* ══ MODALS ══ */}
      {addSlotOpen && selectedRound && (
        <AddSlotModal
          roundId={selectedRound.id}
          expectedClosingDate={
            roundDetail?.jobPostClosingDateTime ||
            selectedRound?.jobPostClosingDateTime
          }
          existingSlots={roundDetail?.slots || []}
          onClose={() => setAddSlotOpen(false)}
          onSaved={() => {
            setAddSlotOpen(false);
            fetchRoundDetail();
          }}
        />
      )}

      {editSlot && (
        <EditSlotModal
          slot={editSlot}
          expectedClosingDate={
            roundDetail?.jobPostClosingDateTime ||
            selectedRound?.jobPostClosingDateTime
          }
          existingSlots={roundDetail?.slots || []}
          onClose={() => setEditSlot(null)}
          onSaved={() => {
            setEditSlot(null);
            fetchRoundDetail();
          }}
        />
      )}

      {updateStatusTarget && (
        <UpdateInterviewStatusModal
          interview={updateStatusTarget}
          onClose={() => setUpdateStatusTarget(null)}
          onSaved={(responseData) => {
            setUpdateStatusTarget(null);
            fetchInterviews();
            if (responseData?.url) setNextRoundData(responseData);
          }}
        />
      )}

      {nextRoundData && (
        <NextRoundUrlModal
          data={nextRoundData}
          onClose={() => setNextRoundData(null)}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Interview Status Badge + constants
// ─────────────────────────────────────────────────────────────────────────────
const INTERVIEW_STATUS_BADGE = {
  PENDING: { label: "Pending", color: "#d97706", bg: "#fef3c7" },
  SCHEDULED: { label: "Scheduled", color: "#2563eb", bg: "#dbeafe" },
  SHORTLISTED: { label: "Shortlisted", color: "#16a34a", bg: "#dcfce7" },
  PROCESSING: { label: "Processing", color: "#7c3aed", bg: "#ede9fe" },
  HOLD: { label: "Hold", color: "#d97706", bg: "#fef3c7" },
  REJECTED: { label: "Rejected", color: "#dc2626", bg: "#fee2e2" },
  SELECTED: { label: "Selected", color: "#16a34a", bg: "#dcfce7" },
};

const ACTIONABLE_STATUSES = ["SCHEDULED", "HOLD"];
const COMPLETED_STATUSES = ["SHORTLISTED", "REJECTED", "SELECTED"];

// ─────────────────────────────────────────────────────────────────────────────
//  Interview Table Section
// ─────────────────────────────────────────────────────────────────────────────
const InterviewTableSection = ({
  title,
  icon,
  interviews,
  showUpdateBtn,
  onUpdateStatus,
  accentColor,
}) => {
  if (interviews.length === 0) return null;
  return (
    <div className="si-interview-section" style={{ marginBottom: 28 }}>
      <div
        className="si-interview-section__header"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          paddingBottom: 8,
          borderBottom: `2px solid ${accentColor}22`,
        }}
      >
        <span style={{ fontSize: 16 }}>{icon}</span>
        <h3
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: accentColor,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            margin: 0,
          }}
        >
          {title}
        </h3>
        <span
          style={{
            marginLeft: 4,
            padding: "2px 8px",
            background: `${accentColor}18`,
            color: accentColor,
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {interviews.length}
        </span>
      </div>
      <div className="cand-table-wrapper">
        <table className="cand-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Candidate</th>
              <th>Job Post</th>
              <th>Round</th>
              <th>Mode</th>
              <th>Interviewer</th>
              <th>Scheduled At</th>
              <th>Status</th>
              <th>Comments</th>
              {showUpdateBtn && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {interviews.map((interview, i) => {
              const sm = INTERVIEW_STATUS_BADGE[
                (interview.status || "").toUpperCase()
              ] || {
                label: interview.status || "—",
                color: "#6b7280",
                bg: "#f3f4f6",
              };
              return (
                <tr key={interview.interviewHistoryId}>
                  <td className="cand-td-num">{i + 1}</td>
                  <td>
                    <div className="cand-identity">
                      <div
                        className="cand-avatar"
                        style={{
                          background: avatarColor(
                            interview.candidateName || "",
                          ),
                          width: 28,
                          height: 28,
                          fontSize: 11,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(interview.candidateName || "")}
                      </div>
                      <div>
                        <div className="cand-name">
                          {interview.candidateName || "—"}
                        </div>
                        <div className="cand-email">
                          {interview.candidateEmail || ""}
                        </div>
                        {interview.candidatePhone && (
                          <div className="cand-email">
                            {interview.candidatePhone}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    {interview.jobPostTitle ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "3px 8px",
                          background: "#f0f9ff",
                          border: "1px solid #bae6fd",
                          borderRadius: 12,
                          fontSize: 12,
                          color: "#0369a1",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <rect
                            x="1"
                            y="3"
                            width="10"
                            height="7"
                            rx="1.2"
                            stroke="currentColor"
                            strokeWidth="1.2"
                          />
                          <path
                            d="M4 3V2a2 2 0 014 0v1"
                            stroke="currentColor"
                            strokeWidth="1.2"
                          />
                        </svg>
                        {interview.jobPostTitle}
                      </span>
                    ) : (
                      <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>
                    <div className="cand-name">
                      {interview.roundName || "—"}
                    </div>
                  </td>
                  <td>
                    {interview.roundMode ? (
                      <span className="si-round-mode-chip">
                        {interview.roundMode}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <div className="cand-identity">
                      <div
                        className="cand-avatar"
                        style={{
                          background: avatarColor(
                            interview.interviewerName || "",
                          ),
                        }}
                      >
                        {getInitials(interview.interviewerName || "")}
                      </div>
                      <div className="cand-name">
                        {interview.interviewerName || "—"}
                      </div>
                    </div>
                  </td>
                  <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                    {fmt(interview.interviewDateTime)}
                  </td>
                  <td>
                    <span
                      className="cand-istatus"
                      style={{ color: sm.color, background: sm.bg }}
                    >
                      {sm.label}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, color: "#6b7280", maxWidth: 160 }}>
                    {interview.comment || "—"}
                  </td>
                  {showUpdateBtn && (
                    <td>
                      <button
                        className="iv-action-btn iv-action-btn--update-status"
                        onClick={() => onUpdateStatus(interview)}
                        title="Update Interview Status"
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 13 13"
                          fill="none"
                        >
                          <path
                            d="M2 6.5l3.5 3.5 5.5-5.5"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Update Interview
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  My Interviews Tab
// ─────────────────────────────────────────────────────────────────────────────
const MyInterviewsTab = ({
  interviews,
  loading,
  onRefresh,
  onUpdateStatus,
}) => {
  const [search, setSearch] = useState("");
  const [jobFilter, setJobFilter] = useState("");

  const jobOptions = useMemo(() => {
    const map = new Map();
    interviews.forEach((i) => {
      if (i.jobPostId && i.jobPostTitle)
        map.set(String(i.jobPostId), i.jobPostTitle);
    });
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [interviews]);

  const filtered = interviews.filter((i) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      i.roundName?.toLowerCase().includes(q) ||
      i.interviewerName?.toLowerCase().includes(q) ||
      i.status?.toLowerCase().includes(q) ||
      i.jobPostTitle?.toLowerCase().includes(q);
    const matchJob = !jobFilter || String(i.jobPostId) === jobFilter;
    return matchSearch && matchJob;
  });

  const actionable = filtered.filter((i) =>
    ACTIONABLE_STATUSES.includes((i.status || "").toUpperCase()),
  );
  const shortlisted = filtered.filter(
    (i) => (i.status || "").toUpperCase() === "SHORTLISTED",
  );
  const selected = filtered.filter(
    (i) => (i.status || "").toUpperCase() === "SELECTED",
  );
  const rejected = filtered.filter(
    (i) => (i.status || "").toUpperCase() === "REJECTED",
  );
  const others = filtered.filter((i) => {
    const s = (i.status || "").toUpperCase();
    return !ACTIONABLE_STATUSES.includes(s) && !COMPLETED_STATUSES.includes(s);
  });

  const totalCount = interviews.length;

  return (
    <div className="si-interviews-tab">
      <div className="cand-page__header" style={{ marginBottom: 16 }}>
        <div>
          <h2 className="cand-page__title">My Interviews</h2>
          <p className="cand-page__sub">
            {totalCount} interview{totalCount !== 1 ? "s" : ""} assigned to you
          </p>
        </div>
        <button className="si-refresh-btn" onClick={onRefresh} title="Refresh">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path
              d="M2 8a6 6 0 1 0 1.5-3.9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <path
              d="M2 4v4h4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Refresh
        </button>
      </div>

      <JobPostFilterBar
        jobs={jobOptions}
        selected={jobFilter}
        onSelect={setJobFilter}
        totalCount={totalCount}
        filteredCount={filtered.length}
      />

      <div className="cand-search-wrap">
        <svg
          className="cand-search-icon"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <circle
            cx="6.5"
            cy="6.5"
            r="4.5"
            stroke="#9ca3af"
            strokeWidth="1.5"
          />
          <path
            d="M10.5 10.5l3 3"
            stroke="#9ca3af"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
        <input
          className="cand-search"
          placeholder="Search by job, round, interviewer or status..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="cand-search-clear" onClick={() => setSearch("")}>
            ×
          </button>
        )}
      </div>

      {loading ? (
        <div className="cand-loader">Loading your interviews…</div>
      ) : filtered.length === 0 ? (
        <div className="cand-empty">
          <span className="cand-empty__icon">📋</span>
          <p>
            {search || jobFilter
              ? "No interviews match your filters."
              : "No interviews assigned to you yet."}
          </p>
        </div>
      ) : (
        <>
          <InterviewTableSection
            title="Active — Pending Outcome"
            icon="🗓️"
            interviews={actionable}
            showUpdateBtn={true}
            onUpdateStatus={onUpdateStatus}
            accentColor="#2563eb"
          />
          <InterviewTableSection
            title="Shortlisted"
            icon="✅"
            interviews={shortlisted}
            showUpdateBtn={false}
            onUpdateStatus={onUpdateStatus}
            accentColor="#16a34a"
          />
          <InterviewTableSection
            title="Selected"
            icon="🏆"
            interviews={selected}
            showUpdateBtn={false}
            onUpdateStatus={onUpdateStatus}
            accentColor="#0ea5e9"
          />
          <InterviewTableSection
            title="Rejected"
            icon="❌"
            interviews={rejected}
            showUpdateBtn={false}
            onUpdateStatus={onUpdateStatus}
            accentColor="#dc2626"
          />
          <InterviewTableSection
            title="Other"
            icon="📋"
            interviews={others}
            showUpdateBtn={false}
            onUpdateStatus={onUpdateStatus}
            accentColor="#6b7280"
          />
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Update Interview Status Modal
// ─────────────────────────────────────────────────────────────────────────────
const UpdateInterviewStatusModal = ({ interview, onClose, onSaved }) => {
  const [form, setForm] = useState({
    status: interview.status || "SHORTLISTED",
    comments: interview.comment || "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.status) {
      toast.error("Please select a status.");
      return;
    }
    setLoading(true);
    try {
      const payload = [
        {
          interviewHistoryId: interview.interviewHistoryId,
          status: form.status,
          comments: form.comments || undefined,
        },
      ];
      const res = await axios.put(
        `${EMP_INTERVIEW_HISTORY_API}/update-interview-status`,
        payload,
      );
      toast.success("Interview status updated successfully.");
      const responseDataItem = res?.data?.data?.[0] || null;
      onSaved(responseDataItem);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to update interview status.",
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedStatusInfo =
    INTERVIEW_STATUS_BADGE[(form.status || "").toUpperCase()];

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">Update Interview Status</h2>
            <p className="drawer__sub">
              Round:{" "}
              <strong>{interview.roundName || `#${interview.roundId}`}</strong>
            </p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="drawer__body">
          <div className="si-update-interviewer">
            <div
              className="cand-avatar"
              style={{
                background: avatarColor(interview.interviewerName || ""),
                width: 32,
                height: 32,
                fontSize: 12,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {getInitials(interview.interviewerName || "")}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {interview.interviewerName || "—"}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Interview History #{interview.interviewHistoryId}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px",
              marginTop: 10,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 8,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: avatarColor(interview.candidateName || ""),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {getInitials(interview.candidateName || "")}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#166534" }}>
                {interview.candidateName || "—"}
              </div>
              <div style={{ fontSize: 12, color: "#4b7c59" }}>
                {interview.candidateEmail || ""}
                {interview.candidatePhone
                  ? ` · ${interview.candidatePhone}`
                  : ""}
              </div>
            </div>
            <span
              style={{
                marginLeft: "auto",
                padding: "3px 8px",
                background: "#dcfce7",
                border: "1px solid #86efac",
                borderRadius: 20,
                fontSize: 11,
                color: "#15803d",
              }}
            >
              Candidate
            </span>
          </div>

          <div
            style={{
              display: "flex",
              gap: 12,
              marginTop: 16,
              padding: "10px 14px",
              background: "#f0f9ff",
              border: "1px solid #bae6fd",
              borderRadius: 8,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              style={{ flexShrink: 0, marginTop: 1, color: "#0369a1" }}
            >
              <circle
                cx="8"
                cy="8"
                r="6.5"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path
                d="M8 5v3.2l2 1.3"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#0369a1",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  marginBottom: 6,
                }}
              >
                Interview Time
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div>
                  <div
                    style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}
                  >
                    Start
                  </div>
                  <div
                    style={{ fontSize: 13, fontWeight: 600, color: "#0c4a6e" }}
                  >
                    {fmt(interview.interviewDateTime) || "—"}
                  </div>
                </div>
                {(interview.interviewEndDateTime ||
                  interview.slotEndDateTime) && (
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#64748b",
                        marginBottom: 2,
                      }}
                    >
                      End
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#0c4a6e",
                      }}
                    >
                      {fmt(
                        interview.interviewEndDateTime ||
                          interview.slotEndDateTime,
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="cand-field" style={{ marginTop: 16 }}>
            <label className="cand-field__label">
              Interview Outcome <span className="cand-field__req"> *</span>
            </label>
            <div className="si-status-picker">
              {INTERVIEW_STATUS_OPTIONS.map((opt) => {
                const info = INTERVIEW_STATUS_BADGE[opt.value] || {};
                const isSelected = form.status === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`si-status-pill${isSelected ? " si-status-pill--selected" : ""}`}
                    style={
                      isSelected
                        ? {
                            color: info.color,
                            background: info.bg,
                            borderColor: info.color,
                          }
                        : {}
                    }
                    onClick={() =>
                      setForm((p) => ({ ...p, status: opt.value }))
                    }
                  >
                    {isSelected && (
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2.5 6l2.5 2.5L9.5 3"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="cand-field" style={{ marginTop: 14 }}>
            <label className="cand-field__label">Comments / Feedback</label>
            <textarea
              className="cand-input"
              rows={3}
              placeholder="Add your interview notes or feedback..."
              value={form.comments}
              onChange={(e) =>
                setForm((p) => ({ ...p, comments: e.target.value }))
              }
              style={{ resize: "vertical", minHeight: 80 }}
            />
          </div>
        </div>
        <div className="drawer__footer">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="cv-btn cv-btn--submit"
            onClick={handleSubmit}
            disabled={loading}
            style={
              selectedStatusInfo ? { background: selectedStatusInfo.color } : {}
            }
          >
            {loading ? (
              <span className="cv-btn__spinner" />
            ) : (
              `Mark as ${INTERVIEW_STATUS_OPTIONS.find((o) => o.value === form.status)?.label || form.status}`
            )}
          </button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Add Slot Modal — full-width, multi-slot week calendar
// ─────────────────────────────────────────────────────────────────────────────
const AddSlotModal = ({
  roundId,
  expectedClosingDate,
  existingSlots = [],
  onClose,
  onSaved,
}) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${_pad(now.getMonth() + 1)}-${_pad(now.getDate())}T${_pad(now.getHours())}:${_pad(now.getMinutes())}`;
  }, []);

  const maxDateStr = useMemo(() => {
    if (!expectedClosingDate) return undefined;
    const d = new Date(expectedClosingDate);
    return `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}T23:59`;
  }, [expectedClosingDate]);

  const handleSubmit = async () => {
    if (slots.length === 0) {
      toast.error("Add at least one slot by clicking on the calendar.");
      return;
    }
    setLoading(true);
    try {
      const payload = slots.map((s) => {
        const startParts = s.slotStartDateTime.match(
          /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/,
        );
        const endParts = s.slotEndDateTime.match(
          /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/,
        );
        const [, sY, sM, sD, sH, sMi] = startParts.map(Number);
        const [, eY, eM, eD, eH, eMi] = endParts.map(Number);
        const startIso = new Date(sY, sM - 1, sD, sH, sMi, 0).toISOString();
        const endIso = new Date(eY, eM - 1, eD, eH, eMi, 0).toISOString();
        const durationMins =
          s.durationMinutes ||
          calcDurationMinutes(s.slotStartDateTime, s.slotEndDateTime);
        return {
          slotStartDateTime: startIso,
          slotEndDateTime: endIso,
          ...(durationMins ? { duration: toIsoDuration(durationMins) } : {}),
        };
      });
      await axios.post(`${EMP_SCHEDULE_API}/${roundId}/slots`, payload);
      toast.success(
        `${slots.length} slot${slots.length > 1 ? "s" : ""} added successfully.`,
      );
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add slots.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Full-screen backdrop */}
      <div
        className="drawer-backdrop"
        onClick={onClose}
        style={{ zIndex: 900 }}
      />

      {/* Full-width modal panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 20px",
            borderBottom: "1px solid #e5e7eb",
            background: "#fff",
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Add Availability Slots
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>
              Set duration · then click anywhere on the calendar to place slots
              · click a block to remove it
            </p>
          </div>

          {expectedClosingDate && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: 8,
                fontSize: 11,
                color: "#92400e",
                flexShrink: 0,
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="none"
                style={{ flexShrink: 0 }}
              >
                <circle
                  cx="8"
                  cy="8"
                  r="6.5"
                  stroke="#d97706"
                  strokeWidth="1.3"
                />
                <path
                  d="M8 5v3.5M8 10.5v.5"
                  stroke="#d97706"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              Closes{" "}
              <strong>
                {new Date(expectedClosingDate).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </strong>
            </div>
          )}

          <button
            onClick={onClose}
            style={{
              border: "1px solid #e5e7eb",
              background: "#fff",
              borderRadius: 8,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
              color: "#6b7280",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Calendar body — takes all remaining height */}
        <div
          style={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MultiSlotCalendar
            slots={slots}
            onSlotsChange={setSlots}
            minDateStr={todayStr}
            maxDateStr={maxDateStr}
            existingSlots={existingSlots}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 20px",
            borderTop: "1px solid #e5e7eb",
            background: "#fff",
            flexShrink: 0,
          }}
        >
          <button
            className="cv-btn cv-btn--ghost"
            onClick={onClose}
            style={{ minWidth: 80 }}
          >
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          {slots.length > 0 && (
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {slots.length} slot{slots.length > 1 ? "s" : ""} ready to save
            </span>
          )}
          <button
            className="cv-btn cv-btn--submit"
            onClick={handleSubmit}
            disabled={loading || slots.length === 0}
            style={{ minWidth: 120, opacity: slots.length === 0 ? 0.5 : 1 }}
          >
            {loading ? (
              <span className="cv-btn__spinner" />
            ) : (
              `Save ${slots.length > 0 ? slots.length : ""} Slot${slots.length !== 1 ? "s" : ""}`
            )}
          </button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Edit Slot Modal  ← UPDATED with duration picker
// ─────────────────────────────────────────────────────────────────────────────
const EditSlotModal = ({
  slot,
  expectedClosingDate,
  existingSlots = [],
  onClose,
  onSaved,
}) => {
  // Don't let a slot block against itself while it's being moved.
  const otherExistingSlots = useMemo(
    () => existingSlots.filter((s) => s.id !== slot?.id),
    [existingSlots, slot],
  );
  // Derive initial duration from existing slot times
  const initialDuration = useMemo(() => {
    if (!slot.slotStartDateTime || !slot.slotEndDateTime) return null;
    const mins = calcDurationMinutes(
      fmtForInput(slot.slotStartDateTime),
      fmtForInput(slot.slotEndDateTime),
    );
    const matched = DURATION_OPTIONS.find((o) => o.minutes === mins);
    return matched ? mins : null;
  }, [slot]);

  const [form, setForm] = useState({
    slotStartDateTime: fmtForInput(slot.slotStartDateTime),
    slotEndDateTime: fmtForInput(slot.slotEndDateTime),
    durationMinutes: initialDuration,
  });
  const [loading, setLoading] = useState(false);

  const todayStr = useMemo(() => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  }, []);

  const maxDateStr = useMemo(() => {
    if (!expectedClosingDate) return undefined;
    const d = new Date(expectedClosingDate);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T23:59`;
  }, [expectedClosingDate]);

  const handleSubmit = async () => {
    if (!form.slotStartDateTime || !form.slotEndDateTime) {
      toast.error("Please fill both start and end times.");
      return;
    }
    if (new Date(form.slotEndDateTime) <= new Date(form.slotStartDateTime)) {
      toast.error("End time must be after start time.");
      return;
    }
    setLoading(true);
    try {
      const startParts = form.slotStartDateTime.match(
        /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/,
      );
      const endParts = form.slotEndDateTime.match(
        /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/,
      );
      const [, sY, sM, sD, sH, sMi] = startParts.map(Number);
      const [, eY, eM, eD, eH, eMi] = endParts.map(Number);
      const startIso = new Date(sY, sM - 1, sD, sH, sMi, 0).toISOString();
      const endIso = new Date(eY, eM - 1, eD, eH, eMi, 0).toISOString();
      const durationMins =
        form.durationMinutes ||
        calcDurationMinutes(form.slotStartDateTime, form.slotEndDateTime);
      await axios.put(`${EMP_SCHEDULE_API}/slots/${slot.id}`, {
        slotStartDateTime: startIso,
        slotEndDateTime: endIso,
        ...(durationMins ? { duration: toIsoDuration(durationMins) } : {}),
      });
      toast.success("Slot updated.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update slot.");
    } finally {
      setLoading(false);
    }
  };

  const durationDisplay = useMemo(() => {
    const mins = calcDurationMinutes(
      form.slotStartDateTime,
      form.slotEndDateTime,
    );
    if (!mins || mins <= 0) return null;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`;
  }, [form.slotStartDateTime, form.slotEndDateTime]);

  // Adapt form state to slots array for MultiSlotCalendar
  const editSlots = useMemo(() => {
    if (!form.slotStartDateTime || !form.slotEndDateTime) return [];
    return [
      {
        slotStartDateTime: form.slotStartDateTime,
        slotEndDateTime: form.slotEndDateTime,
        durationMinutes: form.durationMinutes,
      },
    ];
  }, [form]);

  const handleEditSlotsChange = (newSlots) => {
    if (newSlots.length === 0) {
      setForm((p) => ({
        ...p,
        slotStartDateTime: "",
        slotEndDateTime: "",
        durationMinutes: null,
      }));
    } else {
      const s = newSlots[newSlots.length - 1]; // take latest
      setForm((p) => ({
        ...p,
        slotStartDateTime: s.slotStartDateTime,
        slotEndDateTime: s.slotEndDateTime,
        durationMinutes: s.durationMinutes,
      }));
    }
  };

  return (
    <>
      <div
        className="drawer-backdrop"
        onClick={onClose}
        style={{ zIndex: 900 }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 20px",
            borderBottom: "1px solid #e5e7eb",
            background: "#fff",
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Edit Slot
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>
              Set duration · click on the calendar to move this slot to a new
              time
            </p>
          </div>
          {expectedClosingDate && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: 8,
                fontSize: 11,
                color: "#92400e",
                flexShrink: 0,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                <circle
                  cx="8"
                  cy="8"
                  r="6.5"
                  stroke="#d97706"
                  strokeWidth="1.3"
                />
                <path
                  d="M8 5v3.5M8 10.5v.5"
                  stroke="#d97706"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              Closes{" "}
              <strong>
                {new Date(expectedClosingDate).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </strong>
            </div>
          )}
          <button
            onClick={onClose}
            style={{
              border: "1px solid #e5e7eb",
              background: "#fff",
              borderRadius: 8,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
              color: "#6b7280",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Calendar body */}
        <div
          style={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MultiSlotCalendar
            slots={editSlots}
            onSlotsChange={handleEditSlotsChange}
            minDateStr={todayStr}
            maxDateStr={maxDateStr}
            existingSlots={otherExistingSlots}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 20px",
            borderTop: "1px solid #e5e7eb",
            background: "#fff",
            flexShrink: 0,
          }}
        >
          <button
            className="cv-btn cv-btn--ghost"
            onClick={onClose}
            style={{ minWidth: 80 }}
          >
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          {durationDisplay && form.slotStartDateTime && (
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {new Date(form.slotStartDateTime).toLocaleString("en-IN", {
                weekday: "short",
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}{" "}
              · {durationDisplay}
            </span>
          )}
          <button
            className="cv-btn cv-btn--submit"
            onClick={handleSubmit}
            disabled={loading || !form.slotStartDateTime}
            style={{
              minWidth: 120,
              opacity: !form.slotStartDateTime ? 0.5 : 1,
            }}
          >
            {loading ? <span className="cv-btn__spinner" /> : "Save Changes"}
          </button>
        </div>
      </div>
    </>
  );
};

export default ScheduleInterviewPage;
